import React, { useEffect, useState, useRef } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  orderBy,
  limit
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../firebase";
import * as XLSX from "xlsx";
import Layout from "../components/Layout";

const WarehouseClose = () => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [clientId, setClientId] = useState(""); 

  // Cache for SKU Master
  const [skuList, setSkuList] = useState([]);
  const [skuSortOrder, setSkuSortOrder] = useState({});
  const [skuHsnMap, setSkuHsnMap] = useState({});
  const [initialOpeningMap, setInitialOpeningMap] = useState({});

  const isSuperAdmin = userProfile?.role?.toLowerCase()?.trim() === "superadmin";

  useEffect(() => {
    onAuthStateChanged(auth, async (user) => {
      if (!user) return;
      const snap = await getDoc(doc(db, "users", user.uid));
      if (snap.exists()) {
        const profile = snap.data();
        setUserProfile(profile);
        setClientId(profile.clientId || "mdsons");
      }
    });
  }, []);

  useEffect(() => {
    if (skuList.length > 0 && date) {
      loadDateData(date);
    }
  }, [date, skuList]);

  const loadSkuMaster = async (cid) => {
    if (!cid) return;
    const snap = await getDocs(query(collection(db, "skuMaster"), where("clientId", "==", cid)));
    const list = [];
    const sortMap = {};
    const openingMap = {};
    const hsnMap = {};
    snap.forEach((d) => {
      const data = d.data();
      if (data.sku) {
        list.push(data.sku);
        sortMap[data.sku] = Number(data.sortorder ?? 9999);
        openingMap[data.sku] = Number(data.openingstock || 0);
        hsnMap[data.sku] = data.hsn || "";
      }
    });

    const customSort = (a, b) => {
      const hsnA = Number(hsnMap[a] || 0);
      const hsnB = Number(hsnMap[b] || 0);
      if (hsnA !== hsnB) {
        return hsnB - hsnA; // HIGH to LOW
      }
      const sortA = sortMap[a] || 9999;
      const sortB = sortMap[b] || 9999;
      return sortA - sortB; // LOW to HIGH
    };

    setSkuList(list.sort(customSort));
    setSkuSortOrder(sortMap);
    setSkuHsnMap(hsnMap);
    setInitialOpeningMap(openingMap);
  };

  useEffect(() => {
    if (clientId) loadSkuMaster(clientId);
  }, [clientId]);

  const getPreviousDate = (dateStr) => {
    const d = new Date(dateStr);
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  };

  const getMonthStartDate = (dateStr) => {
    return dateStr.slice(0, 8) + "01";
  };

  const round2 = (num) => Math.round((Number(num) + Number.EPSILON) * 100) / 100;

  const loadDateData = async (selectedDate) => {
    setLoading(true);
    try {
      // 1. Fetch Today's Stock Records (Ledger)
      const ledgerSnap = await getDocs(query(
        collection(db, "daily_stock"), 
        where("clientId", "==", clientId),
        where("date", "==", selectedDate)
      ));
      const ledgerMap = {};
      ledgerSnap.forEach(d => ledgerMap[d.data().sku] = { id: d.id, ...d.data() });

      // 1.5 DEEP LOOKBACK: Find the MOST RECENT record before selectedDate
      const yestSnap = await getDocs(query(
        collection(db, "daily_stock"),
        where("clientId", "==", clientId),
        where("date", "<", selectedDate),
        orderBy("date", "desc"),
        limit(skuList.length > 0 ? skuList.length * 2 : 500) // Fetch enough to cover all SKUs for the last active day
      ));
      
      const yestMap = {};
      // Since we order by date desc, we take the first (most recent) record for each SKU
      yestSnap.forEach(d => {
        const data = d.data();
        if (!yestMap[data.sku]) {
          yestMap[data.sku] = data;
        }
      });

      // 2. Fetch Live Transactions for "Today" (only if not closed)
      const [todayInwSnap, todayInvSnap] = await Promise.all([
        getDocs(query(collection(db, "inward_register"), where("clientId", "==", clientId), where("inwardDate", "==", selectedDate))),
        getDocs(query(collection(db, "orderLines"), where("clientId", "==", clientId), where("approvedDateOnly", "==", selectedDate), where("status", "==", "APPROVED")))
      ]);

      const liveInwMap = {};
      const liveInvMap = {};
      todayInwSnap.forEach(d => liveInwMap[d.data().sku] = (liveInwMap[d.data().sku] || 0) + Number(d.data().quantity || 0));
      todayInvSnap.forEach(d => liveInvMap[d.data().sku] = (liveInvMap[d.data().sku] || 0) + Number(d.data().qty || 0));

      // 3. Assemble Rows
      const allSkus = new Set([...skuList, ...Object.keys(ledgerMap), ...Object.keys(liveInwMap), ...Object.keys(liveInvMap)]);
      
      const finalRows = Array.from(allSkus).map(sku => {
        const isMaster = skuList.includes(sku);
        const ledger = ledgerMap[sku] || {};
        const isClosed = ledger.dayClosed || false;

        // OPENING: 1. Trust current ledger 2. Else Yesterday's Physical 3. Else Master opening
        let opening = 0;
        if (ledger.openingQty !== undefined) {
          opening = Number(ledger.openingQty);
        } else if (yestMap[sku]) {
          // If yesterday was closed, use physical, else use system closing
          opening = Number(yestMap[sku].physicalQty || yestMap[sku].systemClosing || 0);
        } else {
          opening = Number(initialOpeningMap[sku] || 0);
        }
        opening = round2(opening);

        // INWARD/INVOICE: Use Ledger if closed, else use Live transactions
        const inward = round2(isClosed ? Number(ledger.inwardQty || 0) : (liveInwMap[sku] || 0));
        const invoice = round2(isClosed ? Number(ledger.invoiceQty || ledger.outwardQty || 0) : (liveInvMap[sku] || 0));

        // MTD: Pre-calculated MTD + Today's Live activity
        const inwardMTD = round2(Number(ledger.mtdInward || 0) + inward);
        const invoiceMTD = round2(Number(ledger.mtdInvoice || ledger.mtdSales || 0) + invoice);

        const systemClosing = round2(opening + inward - invoice);
        const physical = ledger.physicalQty ?? "";
        const damage = ledger.damageQty ?? "";
        const variance = round2(systemClosing - (Number(physical) || 0) - (Number(damage) || 0));

        let remark = ledger.remarks || "";
        if (variance !== 0 && !remark) remark = `Variance of ${variance.toFixed(2)} detected`;

        return {
          sku,
          hsn: skuHsnMap[sku] || "",
          isMaster,
          openingQty: opening,
          inwardQty: inward,
          inwardMTD: inwardMTD,
          invoiceQty: invoice,
          invoiceMTD: invoiceMTD,
          systemClosing: systemClosing,
          physicalQty: physical,
          damageQty: damage,
          variance: variance || 0,
          remarks: remark,
          dayClosed: isClosed,
          id: ledger.id || `${selectedDate}_${sku}`
        };
      });

      // Sort by HSN (HIGH to LOW), then Master Sort Order (LOW to HIGH)
      finalRows.sort((a, b) => {
        const hsnA = Number(skuHsnMap[a.sku] || 0);
        const hsnB = Number(skuHsnMap[b.sku] || 0);
        if (hsnA !== hsnB) {
          return hsnB - hsnA; // HIGH to LOW
        }
        const sortA = skuSortOrder[a.sku] || 9999;
        const sortB = skuSortOrder[b.sku] || 9999;
        return sortA - sortB; // LOW to HIGH
      });
      setRows(finalRows);
    } catch (err) {
      console.error("Error loading warehouse data:", err);
    }
    setLoading(false);
  };

  const handleInputChange = (idx, field, value) => {
    const newRows = [...rows];
    newRows[idx][field] = value;

    if (field === "openingQty") {
      const r = newRows[idx];
      const op = Number(value) || 0;
      r.systemClosing = round2(op + r.inwardQty - r.invoiceQty);
      
      const phys = Number(r.physicalQty) || 0;
      const dmg = Number(r.damageQty) || 0;
      r.variance = round2(r.systemClosing - phys - dmg);
      
      if (r.variance !== 0) {
        if (!r.remarks || r.remarks.startsWith("Variance of")) {
          r.remarks = `Variance of ${r.variance.toFixed(2)} detected`;
        }
      } else if (r.remarks.startsWith("Variance of")) {
        r.remarks = "";
      }
    }

    // Recalculate variance and remarks if physical or damage changes
    if (field === "physicalQty" || field === "damageQty") {
      const r = newRows[idx];
      const phys = Number(r.physicalQty) || 0;
      const dmg = Number(r.damageQty) || 0;
      r.variance = round2(r.systemClosing - phys - dmg);
      
      if (r.variance !== 0) {
        if (!r.remarks || r.remarks.startsWith("Variance of")) {
          r.remarks = `Variance of ${r.variance.toFixed(2)} detected`;
        }
      } else if (r.remarks.startsWith("Variance of")) {
        r.remarks = "";
      }
    }
    setRows(newRows);
  };

  const saveClosing = async () => {
    setIsProcessing(true);
    try {
      for (const r of rows) {
        const op = Number(r.openingQty) || 0;
        const inw = Number(r.inwardQty) || 0;
        const inv = Number(r.invoiceQty) || 0;
        const systemClosing = round2(op + inw - inv);
        
        const phys = (r.physicalQty === "" || r.physicalQty === undefined || r.physicalQty === null) ? systemClosing : Number(r.physicalQty);
        const dmg = Number(r.damageQty) || 0;
        const variance = round2(systemClosing - phys - dmg);
        
        let remarks = r.remarks || "";
        if (variance !== 0 && (!remarks || remarks.startsWith("Variance of"))) {
          remarks = `Variance of ${variance.toFixed(2)} detected`;
        } else if (variance === 0 && remarks.startsWith("Variance of")) {
          remarks = "";
        }

        const docRef = doc(db, "daily_stock", r.id);
        await setDoc(docRef, {
          clientId: clientId,
          date: date,
          sku: r.sku,
          openingQty: op,
          inwardQty: inw,
          inwardMTD: Number(r.inwardMTD) || 0,
          invoiceQty: inv,
          invoiceMTD: Number(r.invoiceMTD) || 0,
          systemClosing: systemClosing,
          physicalQty: phys,
          damageQty: dmg,
          variance: variance,
          remarks: remarks,
          dayClosed: true,
          updatedAt: serverTimestamp(),
          updatedBy: auth.currentUser?.email || "system"
        }, { merge: true });

        // Carry forward to tomorrow
        const tomorrow = new Date(date);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().slice(0, 10);
        const tomId = `${tomorrowStr}_${r.sku}`;
        
        await setDoc(doc(db, "daily_stock", tomId), {
          clientId: clientId,
          date: tomorrowStr,
          sku: r.sku,
          openingQty: phys, // Carry today's physical as tomorrow's opening
          updatedAt: serverTimestamp()
        }, { merge: true });
      }
      alert("Day Closed Successfully. Tomorrow's Opening Balances initialized.");
      loadDateData(date);
    } catch (err) {
      console.error("Error saving closing:", err);
      alert("Error saving data");
    }
    setIsProcessing(false);
  };

  const saveSingleSku = async (r) => {
    setIsProcessing(true);
    try {
      const op = Number(r.openingQty) || 0;
      const inw = Number(r.inwardQty) || 0;
      const inv = Number(r.invoiceQty) || 0;
      const systemClosing = round2(op + inw - inv);
      
      const phys = (r.physicalQty === "" || r.physicalQty === undefined || r.physicalQty === null) ? systemClosing : Number(r.physicalQty);
      const dmg = Number(r.damageQty) || 0;
      const variance = round2(systemClosing - phys - dmg);
      
      let remarks = r.remarks || "";
      if (variance !== 0 && (!remarks || remarks.startsWith("Variance of"))) {
        remarks = `Variance of ${variance.toFixed(2)} detected`;
      } else if (variance === 0 && remarks.startsWith("Variance of")) {
        remarks = "";
      }

      const docRef = doc(db, "daily_stock", r.id);
      await setDoc(docRef, {
        clientId: clientId,
        date: date,
        sku: r.sku,
        openingQty: op,
        inwardQty: inw,
        inwardMTD: Number(r.inwardMTD) || 0,
        invoiceQty: inv,
        invoiceMTD: Number(r.invoiceMTD) || 0,
        systemClosing: systemClosing,
        physicalQty: phys,
        damageQty: dmg,
        variance: variance,
        remarks: remarks,
        dayClosed: true,
        updatedAt: serverTimestamp(),
        updatedBy: auth.currentUser?.email || "system"
      }, { merge: true });

      // Carry forward to tomorrow
      const tomorrow = new Date(date);
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().slice(0, 10);
      const tomId = `${tomorrowStr}_${r.sku}`;
      
      await setDoc(doc(db, "daily_stock", tomId), {
        clientId: clientId,
        date: tomorrowStr,
        sku: r.sku,
        openingQty: phys, // Carry today's physical as tomorrow's opening
        updatedAt: serverTimestamp()
      }, { merge: true });

      alert(`SKU ${r.sku} Updated Successfully.`);
      loadDateData(date);
    } catch (err) {
      console.error(`Error saving SKU ${r.sku}:`, err);
      alert("Error saving data");
    }
    setIsProcessing(false);
  };

  const exportToExcel = () => {
    const data = rows.map(r => ({
      Date: date,
      HSN: r.hsn || "",
      SKU: r.sku,
      "Opening Qty": r.openingQty,
      "Inward Qty": r.inwardQty,
      "MTD Inward": r.inwardMTD,
      "Invoice Qty": r.invoiceQty,
      "MTD Invoice": r.invoiceMTD,
      "System Closing": r.systemClosing,
      "Physical Qty": r.physicalQty,
      "Damage Qty": r.damageQty,
      "Variance": r.variance,
      "Remarks": r.remarks
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory_Closing");
    XLSX.writeFile(wb, `Stock_Closing_${date}.xlsx`);
  };

  const sumColumn = (col) => {
    return round2(rows.reduce((acc, r) => acc + (Number(r[col]) || 0), 0)).toFixed(2);
  };

  return (
    <Layout>
      <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: "700", color: "#202124", margin: 0 }}>Warehouse Stock Closing</h1>
            <p style={{ color: "#5f6368", marginTop: "4px" }}>Manage daily stock levels, physical counts, and variances.</p>
          </div>
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={exportToExcel}
              style={{
                padding: "10px 20px",
                backgroundColor: "#f8f9fa",
                color: "#3c4043",
                border: "1px solid #dadce0",
                borderRadius: "6px",
                cursor: "pointer",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px"
              }}>
              Export Excel
            </button>
            <button
               disabled={isProcessing || loading}
               onClick={saveClosing}
               style={{
                 padding: "10px 24px",
                 backgroundColor: "#1a73e8",
                 color: "white",
                 border: "none",
                 borderRadius: "6px",
                 cursor: (isProcessing || loading) ? "not-allowed" : "pointer",
                 fontWeight: "600",
                 boxShadow: "0 1px 2px rgba(60,64,67,0.3)"
               }}>
              {isProcessing ? "Processing..." : "Submit & Close Day"}
            </button>
          </div>
        </div>

        <div style={{ 
          backgroundColor: "white", 
          padding: "24px", 
          borderRadius: "12px", 
          boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
          marginBottom: "24px"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "24px" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <label style={{ fontSize: "12px", color: "#5f6368", fontWeight: "600" }}>Select Closing Date</label>
              <input
                type="date"
                value={date}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => setDate(e.target.value)}
                style={{ padding: "10px 14px", borderRadius: "8px", border: "1px solid #dadce0", fontSize: "15px", outline: "none" }}
              />
            </div>
            <div style={{ flex: 1 }}></div>
            {loading && <div style={{ color: "#1a73e8", fontWeight: "600" }}>Refining calculations...</div>}
          </div>

          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", color: "#3c4043", fontSize: "13px" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8f9fa", textAlign: "left" }}>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee", width: "120px" }}>HSN</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee", width: "200px" }}>SKU Item</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee" }}>Opening</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee" }}>Inward</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee", color: "#188038" }}>MTD Inward</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee" }}>Invoice</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee", color: "#d93025" }}>MTD Invoice</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee", fontWeight: "700" }}>Sys Closing</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee", width: "100px" }}>Physical</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee", width: "100px" }}>Damage</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee" }}>Variance</th>
                  <th style={{ padding: "12px", borderBottom: "2px solid #eee" }}>Remarks</th>
                  {isSuperAdmin && <th style={{ padding: "12px", borderBottom: "2px solid #eee" }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f1f3f4", background: r.dayClosed ? "#fafafa" : (r.isMaster ? "white" : "#fff5f5") }}>
                    <td style={{ padding: "12px", color: "#5f6368" }}>{r.hsn}</td>
                    <td style={{ padding: "12px", fontWeight: "600" }}>
                        {r.sku}
                        {!r.isMaster && <span style={{ color: "#d93025", fontSize: "10px", display: "block", fontWeight: "400" }}>MISSING FROM MASTER</span>}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {isSuperAdmin && !r.dayClosed ? (
                        <input
                          type="number"
                          value={r.openingQty}
                          onChange={(e) => handleInputChange(i, "openingQty", e.target.value)}
                          placeholder="0"
                          style={{
                            width: "80px",
                            padding: "6px",
                            borderRadius: "4px",
                            border: "1px solid #dadce0"
                          }}
                        />
                      ) : (
                        r.openingQty.toFixed(2)
                      )}
                    </td>
                    <td style={{ padding: "12px" }}>{r.inwardQty.toFixed(2)}</td>
                    <td style={{ padding: "12px", color: "#188038", fontWeight: "500" }}>{r.inwardMTD.toFixed(2)}</td>
                    <td style={{ padding: "12px" }}>{r.invoiceQty.toFixed(2)}</td>
                    <td style={{ padding: "12px", color: "#d93025", fontWeight: "500" }}>{r.invoiceMTD.toFixed(2)}</td>
                    <td style={{ padding: "12px", fontWeight: "700" }}>{r.systemClosing.toFixed(2)}</td>
                    <td style={{ padding: "8px" }}>
                      <input
                        type="number"
                        disabled={r.dayClosed}
                        value={r.physicalQty}
                        onChange={(e) => handleInputChange(i, "physicalQty", e.target.value)}
                        placeholder="0"
                        style={{ width: "80px", padding: "6px", borderRadius: "4px", border: r.variance !== 0 ? "1px solid #d93025" : "1px solid #dadce0" }}
                      />
                    </td>
                    <td style={{ padding: "8px" }}>
                      <input
                        type="number"
                        disabled={r.dayClosed}
                        value={r.damageQty}
                        onChange={(e) => handleInputChange(i, "damageQty", e.target.value)}
                        placeholder="0"
                        style={{ width: "80px", padding: "6px", borderRadius: "4px", border: "1px solid #dadce0" }}
                      />
                    </td>
                    <td style={{ padding: "12px", fontWeight: "700", color: r.variance !== 0 ? "#d93025" : "inherit", background: r.variance !== 0 ? "#fef2f2" : "inherit" }}>
                      {r.variance.toFixed(2)}
                    </td>
                    <td style={{ padding: "8px" }}>
                      <input
                        type="text"
                        disabled={r.dayClosed}
                        value={r.remarks}
                        onChange={(e) => handleInputChange(i, "remarks", e.target.value)}
                        placeholder="Reason for variance..."
                        style={{ width: "100%", padding: "6px", borderRadius: "4px", border: "1px solid #dadce0" }}
                      />
                    </td>
                    {isSuperAdmin && (
                      <td style={{ padding: "8px" }}>
                        <button
                          disabled={isProcessing || loading}
                          onClick={() => saveSingleSku(r)}
                          style={{
                            padding: "6px 12px",
                            backgroundColor: "#188038",
                            color: "white",
                            border: "none",
                            borderRadius: "4px",
                            cursor: (isProcessing || loading) ? "not-allowed" : "pointer",
                            fontWeight: "600",
                            fontSize: "11px"
                          }}
                        >
                          Save SKU
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#f8f9fa", fontWeight: "800", borderTop: "2px solid #ddd" }}>
                    <td></td>
                    <td style={{ padding: "12px" }}>GRAND TOTALS</td>
                    <td style={{ padding: "12px" }}>{sumColumn("openingQty")}</td>
                    <td style={{ padding: "12px" }}>{sumColumn("inwardQty")}</td>
                    <td style={{ padding: "12px", color: "#188038" }}>{sumColumn("inwardMTD")}</td>
                    <td style={{ padding: "12px" }}>{sumColumn("invoiceQty")}</td>
                    <td style={{ padding: "12px", color: "#d93025" }}>{sumColumn("invoiceMTD")}</td>
                    <td style={{ padding: "12px" }}>{sumColumn("systemClosing")}</td>
                    <td style={{ padding: "12px" }}>{sumColumn("physicalQty")}</td>
                    <td style={{ padding: "12px" }}>{sumColumn("damageQty")}</td>
                    <td style={{ padding: "12px", color: Number(sumColumn("variance")) !== 0 ? "#d93025" : "inherit" }}>{sumColumn("variance")}</td>
                    <td></td>
                    {isSuperAdmin && <td></td>}
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default WarehouseClose;
